<script setup>
  import { ref, onMounted, computed } from "vue"
  import FlashMessageService from '@services/FlashMessages'

  const mapContainer = ref(undefined)
  const selectedVillages = ref([])
  const batchName = ref('')
  const surveyorUuid = ref('')
  const surveyors = ref([])
  const isLoadingSurveyors = ref(false)
  // const batchUuid = ref('')
  const batchUuid = ref('5c9d0bb0-6973-4f0a-bf7e-6e0f3ef52699') // Default value, can be changed
  const villageSurveyPayload = ref({
                "record_uuid": "d2ce2e37-6c94-4052-a5f4-7f4697d3c438",
                "admin_code": "TGO-19855224B68133828997509-87587387B31705753648703-79027355B28837360329741-S728266562",
                "attributes": {
                    "are_christians": true,
                    "num_christians": 11,
                },
                "relations": {
                    "churches": [
                        {
                            "record_uuid": "7a1025d9-bc04-43f9-b301-ea47a18db77c",
                            "attributes": {
                                "church_type": "church",
                                "lat": 13.4528670057106,
                                "lng": 19.7793498951492,
                                "num_churches_in_network": 3,
                                "name": "Jehovah",
                                "year_started": 1985,
                                "month_started": 11,
                                "num_believers": 11,
                            }
                        }
                    ]
                }
            })

  const villageSurveyChurchPayload = ref({
                "record_uuid": "7a1025d9-bc04-43f9-b301-ea47a18db77c",
                "village_survey_record_uuid": "d2ce2e37-6c94-4052-a5f4-7f4697d3c438", // TODO: change this to the actual village survey record uuid
                "attributes": {
                    "church_type": "church",
                    "lat": 13.4528670057106,
                    "lng": 19.7793498951492,
                    "num_churches_in_network": 0,
                    "name": "MGCC",
                    "year_started": 1971,
                    "month_started": 11,
                    "num_believers": 11,
                }
            })

const villageSurveyUpdatePayload = ref({})
const villageSurveyChurchUpdatePayload = ref({})

  let mapSession = undefined
  let surveyBatchController = undefined

  // Function to fetch surveyors from the API
  async function fetchSurveyors() {
    if (isLoadingSurveyors.value) return
    isLoadingSurveyors.value = true
    try {
      const response = await fetch('/api/v1/experiment_data/people')
      if (!response.ok) throw new Error('Failed to fetch surveyors')
      const data = await response.json()
      surveyors.value = data
    } catch (error) {
      console.error('Error fetching surveyors:', error)
      FlashMessageService.error('Failed to fetch surveyors: ' + error.message)
    } finally {
      isLoadingSurveyors.value = false
    }
  }

// Function to fetch batches from the API
async function fetchBatches() {
  try {
    const response = await fetch('/api/v1/experiment_data/batches')

    if (!response.ok) throw new Error('Failed to fetch batches')
    const data = await response.json()

    // Get the first result from the response
    if (data && data.length > 0) {
      const firstBatch = data[0]

      // Extract village surveys from the data column
      if (firstBatch.data && firstBatch.data.batch_status && firstBatch.data.batch_status.village_surveys) {
        const villageSurveys = firstBatch.data.batch_status.village_surveys

        // Process each village survey
        villageSurveys.forEach(villageSurvey => {
          // Create village survey update payload
          villageSurveyUpdatePayload.value = {
            "record_uuid": villageSurvey.record_uuid,
            "admin_code": villageSurvey.village?.admin_4_code,
            "attributes": {
              "are_christians": villageSurvey.are_christians,
              "num_christians": 1000,
            },
            "relations": {
              "churches": []
            }
          }

          // Process churches for this village survey
          if (villageSurvey.churches && villageSurvey.churches.length > 0) {
            villageSurvey.churches.forEach(church => {
              // Create village survey church update payload
              villageSurveyChurchUpdatePayload.value = {
                "record_uuid": church.record_uuid,
                "village_survey_record_uuid": villageSurvey.record_uuid,
                "attributes": {
                  "church_type": church.church_type,
                  "lat": church.lat || 0,
                  "lng": church.lng || 0,
                  "num_churches_in_network": church.num_churches_in_network || 0,
                  "name": `${church.name} - UPDATED`,
                  "year_started": church.year_started || null,
                  "month_started": church.month_started || null,
                  "num_believers": church.num_believers || 0,
                }
              }
            })
          }
        })

        console.log('Processed village surveys from batch:', villageSurveys.length)
        console.log('Village Survey Update Payload:', villageSurveyUpdatePayload.value)
        console.log('Village Survey Church Update Payload:', villageSurveyChurchUpdatePayload.value)
      }
    }
  } catch (error) {
    console.error('Error fetching batches:', error)
    FlashMessageService.error('Failed to fetch batches: ' + error.message)
  }
}

  // Function to handle selection changes
  function handleSelectionChange() {
    console.log('Selection change handler called')
    const polygons = surveyBatchController?.selectionGroup?.selectedPolygons
    console.log('Current selection:', polygons)
    if (polygons) {
      selectedVillages.value = polygons.map(polygon => ({
        name: polygon.village?.name,
        population: polygon.village?.population,
      }))
    } else {
      selectedVillages.value = []
    }
  }

  function handleBatchLoad(batch) {
    batchName.value = batch.batchStatus.batch.name
    surveyorUuid.value = batch.batchStatus.batch.currentSurveyorExternalAppUserId
  }

  onMounted(async () => {
    try {
      console.log('Starting map initialization...')
      // Fetch surveyors when component mounts
      await fetchSurveyors()

      // Fetch batches when component mounts
      await fetchBatches()

      // Create the MapSessions for the two maps
      mapSession = window.Kestrel.createMapSession(mapContainer.value, "map-container")
      console.log('Map session created:', mapSession)

      const initResult = await mapSession.initialize({
        loadCountryOptions: false,
        countryIso3Code: 'TGO',
        simplificationStack: { 4: 0 },
        kestrelApiKey: gon.kestrel_api_key,
        kestrelApiUrl: gon.kestrel_api_url,
      })
      console.log('Map session initialized successfully:', initResult)

      // Add zoom controls
      initResult.addZoomControl()
      console.log('Zoom controls added')

      // Initialize the survey batch controller
      try {
        surveyBatchController = new Kestrel.SurveyBatchController(initResult, {
          orgCode: 'TTI',
          batchesBaseUrl: "/api/v1",
          batchUuid: batchUuid.value || undefined,
          shading: { type: 'batch' }  // Add the batch shading configuration
        })
        // Register for selection changes
        surveyBatchController.registerBatchLoadedInformer(handleBatchLoad)
        surveyBatchController.registerPolygonSelectionChangeInformer(handleSelectionChange)

        console.log('Survey batch controller created:', surveyBatchController)
      } catch (error) {
        console.error('Error creating survey batch controller:', error)
      }
    } catch (error) {
      console.error('Error during map initialization:', error)
      FlashMessageService.error('Failed to initialize map: ' + error.message)
    }
  })

  async function createSurveyBatch () {
    if (!surveyBatchController) { return }
    try {
      surveyBatchController.setBatchName(batchName.value)
      await surveyBatchController.createBatch()
      FlashMessageService.success(`Survey batch "${batchName.value}" created successfully`)
    } catch (error) {
      FlashMessageService.error('Failed to create survey batch: ' + error.message)
    }
  }

  async function updateSurveyBatch () {
    if (!surveyBatchController) { return }
    try {
      surveyBatchController.setBatchName(batchName.value)
      await surveyBatchController.updateBatch()
      FlashMessageService.success(`Survey batch "${batchName.value}" updated successfully`)
    } catch (error) {
      FlashMessageService.error('Failed to update survey batch: ' + error.message)
    }
  }

  async function assignSurveyor() {
    if (!surveyBatchController) { return }
    try {
      await surveyBatchController.setSurveyorUuid(surveyorUuid.value)
      FlashMessageService.success('Surveyor assigned successfully')
    } catch (error) {
      FlashMessageService.error('Failed to assign surveyor: ' + error.message)
    }
  }

  async function assignAndUpdateSurveyor() {
    if (!surveyBatchController) { return }
    try {
      await surveyBatchController.updateBatchSurveyor(surveyorUuid.value)
      await updateSurveyBatch()
      FlashMessageService.success('Surveyor assigned and batch updated successfully')
    } catch (error) {
      FlashMessageService.error('Failed to assign surveyor and update batch: ' + error.message)
    }
  }

  async function createVillageSurvey() {
    if (!surveyBatchController) { return }
    await surveyBatchController.createVillageSurvey(villageSurveyPayload.value)
    FlashMessageService.success('Village survey created successfully')
  }

  async function updateVillageSurvey() {
    if (!surveyBatchController) { return }

    // Check if payload is empty or invalid
    if (!villageSurveyUpdatePayload.value || Object.keys(villageSurveyUpdatePayload.value).length === 0) {
      FlashMessageService.error('No village survey data available. Please fetch batches first.')
      return
    }

    try {
      await surveyBatchController.updateVillageSurvey(villageSurveyUpdatePayload.value)
      FlashMessageService.success('Village survey updated successfully.')
    } catch (error) {
      FlashMessageService.error('Failed to update village survey: ' + error.message)
    }
  }

  async function createVillageSurveyChurch() {
    if (!surveyBatchController) { return }
    await surveyBatchController.createVillageSurveyChurch(villageSurveyChurchPayload.value)
    FlashMessageService.success('Village survey church created successfully')
  }

  async function updateVillageSurveyChurch() {
    if (!surveyBatchController) { return }

    // Check if payload is empty or invalid
    if (!villageSurveyChurchUpdatePayload.value || Object.keys(villageSurveyChurchUpdatePayload.value).length === 0) {
      FlashMessageService.error('No village survey church data available. Please fetch batches first.')
      return
    }

    try {
      await surveyBatchController.updateVillageSurveyChurch(villageSurveyChurchUpdatePayload.value)
      FlashMessageService.success('Village survey church updated successfully.')
    } catch (error) {
      FlashMessageService.error('Failed to update village survey church: ' + error.message)
    }
  }
</script>

<template>
  <div class="flex h-screen pl-4">
    <!-- Sidebar -->
    <div class="w-64 flex flex-col border-r border-gray-200">
      <!-- Fixed Header with Buttons -->
      <div class="p-4 backdrop-blur-sm border-b border-gray-200">
        <h2 class="text-lg font-semibold mb-3">Survey Batch Controls</h2>

        <!-- Batch Name Input -->
        <div class="mb-4">
          <label for="batchName" class="block text-sm font-medium text-gray-700 mb-1">Batch Name</label>
          <input type="text" id="batchName" v-model="batchName" placeholder="Enter batch name"
            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm" />
        </div>

        <!-- Surveyor ID Input -->
        <div class="mb-4">
          <label for="surveyorUuid" class="block text-sm font-medium text-gray-700 mb-1">Surveyor</label>
          <div class="space-y-2">
            <select id="surveyorUuid" v-model="surveyorUuid"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
              :disabled="isLoadingSurveyors">
              <option value="">Select a surveyor</option>
              <option v-for="surveyor in surveyors" :key="surveyor.uuid" :value="surveyor.uuid">
                {{ surveyor.name }}
              </option>
            </select>
            <div class="flex flex-col gap-2">
              <button
                class="w-full bg-gray-500/80 hover:bg-gray-600 text-white text-xs font-medium py-1.5 px-2 rounded-md transition-colors duration-200 shadow-sm"
                @click="assignSurveyor" :disabled="!surveyorUuid">
                Assign Surveyor
              </button>
              <button v-if="batchUuid"
                class="w-full bg-gray-500/80 hover:bg-gray-600 text-white text-xs font-medium py-1.5 px-2 rounded-md transition-colors duration-200 shadow-sm"
                @click="assignAndUpdateSurveyor" :disabled="!surveyorUuid">
                Assign & Update Surveyor
              </button>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex gap-2 flex-col">
          <button v-if="!batchUuid"
            class="flex-1 bg-blue-500/90 hover:bg-blue-600 text-white text-sm font-medium py-3 px-3 rounded-md transition-colors duration-200 shadow-sm"
            @click="createSurveyBatch">
            Create
          </button>
          <button v-if="batchUuid"
            class="flex-1 bg-green-500/90 hover:bg-green-600 text-white text-sm font-medium py-3 px-3 rounded-md transition-colors duration-200 shadow-sm"
            @click="updateSurveyBatch">
            Update
          </button>

          <!-- Create Village Survey Button -->
          <button class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            @click="createVillageSurvey" v-if="batchUuid">Create Village Survey</button>

          <!-- Update Village Survey Button -->
          <button
            class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:bg-gray-400 disabled:cursor-not-allowed"
            @click="updateVillageSurvey" v-if="batchUuid"
            :disabled="!villageSurveyUpdatePayload || Object.keys(villageSurveyUpdatePayload).length === 0">
            {{
              (!villageSurveyUpdatePayload || Object.keys(villageSurveyUpdatePayload).length === 0)
                ? 'Update Village Survey(No Data)' : 'Update Village Survey'
            }}
          </button>

          <!-- Create Village Survey Church Button -->
          <button class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            @click="createVillageSurveyChurch" v-if="batchUuid">Create Village Survey Church</button>

          <!-- Update Village Survey Church Button -->
          <button
            class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:bg-gray-400 disabled:cursor-not-allowed"
            @click="updateVillageSurveyChurch" v-if="batchUuid"
            :disabled="!villageSurveyChurchUpdatePayload || Object.keys(villageSurveyChurchUpdatePayload).length === 0">
            {{ (!villageSurveyChurchUpdatePayload || Object.keys(villageSurveyChurchUpdatePayload).length === 0) ?
              'Update Village Survey Church (No Data)' : 'Update Village Survey Church' }}
          </button>

        </div>

        <!-- Debug Section -->
        <div class="mt-4 p-3 bg-gray-100 rounded">
          <h4 class="text-sm font-medium text-gray-700 mb-2">Debug Info</h4>
          <div class="text-xs text-gray-600">
            <div class="mb-2">
              <strong>Village Survey Update Payload:</strong>
              <pre
                class="mt-1 bg-white p-2 rounded text-xs overflow-auto max-h-20">{{ JSON.stringify(villageSurveyUpdatePayload, null, 2) }}</pre>
            </div>
            <div>
              <strong>Village Survey Church Update Payload:</strong>
              <pre
                class="mt-1 bg-white p-2 rounded text-xs overflow-auto max-h-20">{{ JSON.stringify(villageSurveyChurchUpdatePayload, null, 2) }}</pre>
            </div>
          </div>
        </div>

      </div>

      <!-- Scrollable Content Area for Selected Villages -->
      <div class="flex-1 overflow-y-auto p-4">
        <div class="flex justify-between items-center mb-2">
          <h3 class="text-sm font-medium text-gray-700">Selected Villages</h3>
          <span class="text-xs text-gray-500">{{ selectedVillages.length }} selected</span>
        </div>
        <div class="space-y-2">
          <div v-if="selectedVillages.length === 0" class="text-sm text-gray-500 italic">
            No villages selected
          </div>
          <template v-else>
            <div v-for="village in selectedVillages" :key="village.admin4Code"
              class="p-2 bg-white/50 backdrop-blur-sm rounded border border-gray-200 hover:bg-white/70 transition-colors duration-200">
              <div class="font-medium text-gray-800 text-sm mb-1">{{ village.name || 'Unknown Village' }}</div>
              <div class="text-xs text-gray-500">
                Population: {{ village.population?.toLocaleString() || 'N/A' }}
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 flex flex-col">
      <div class="flex-1 relative">
        <div id="map-container" ref="mapContainer" class="absolute inset-0"></div>
      </div>
    </div>
  </div>
</template>


<style scoped>
.slidecontainer {
  width: 100%; /* Width of the outside container */
}

/* The slider itself */
.slider {
  -webkit-appearance: none;  /* Override default CSS styles */
  appearance: none;
  width: 100%; /* Full-width */
  height: 25px; /* Specified height */
  background: #d3d3d3; /* Grey background */
  outline: none; /* Remove outline */
  opacity: 0.7; /* Set transparency (for mouse-over effects on hover) */
  -webkit-transition: .2s; /* 0.2 seconds transition on hover */
  transition: opacity .2s;
}

/* Mouse-over effects */
.slider:hover {
  opacity: 1; /* Fully shown on mouse-over */
}

/* The slider handle (use -webkit- (Chrome, Opera, Safari, Edge) and -moz- (Firefox) to override default look) */
.slider::-webkit-slider-thumb {
  -webkit-appearance: none; /* Override default look */
  appearance: none;
  width: 25px; /* Set a specific slider handle width */
  height: 25px; /* Slider handle height */
  background: #04AA6D; /* Green background */
  cursor: pointer; /* Cursor on hover */
}

.slider::-moz-range-thumb {
  width: 25px; /* Set a specific slider handle width */
  height: 25px; /* Slider handle height */
  background: #04AA6D; /* Green background */
  cursor: pointer; /* Cursor on hover */
}

/* Update sidebar styles */
.sidebar {
  background: transparent;
}

/* Ensure the map container takes full height */
#map-container {
  height: 100vh;
  width: 100%;
}

/* Add styles for the scrollable content */
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 3px;
}

/* Add hover effect for village cards */
.village-card {
  transition: all 0.2s ease-in-out;
}

.village-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}
</style>